import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Instagram, MapPin, Banknote, QrCode, Activity, Target, UserCheck, Trophy, Flame, Clock, ShieldCheck, Crown } from "lucide-react";
import { BRAND, WHATSAPP_LINK, MEDIA, PERSONAL_PLANS, PERSONAL_BENEFITS, PERSONAL_CONDITIONS, FAQ_PERSONAL, ABOUT_PARAGRAPHS } from "@/lib/data";
import SectionTitle from "@/components/SectionTitle";
import Reveal from "@/components/Reveal";
import BeforeAfter from "@/components/BeforeAfter";
import FAQ from "@/components/FAQ";

const iconMap = {
  Activity, Target, UserCheck, Trophy, Flame, Clock,
};

export default function PersonalTrainer() {
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={MEDIA.gymEnv}
            alt=""
            className="absolute inset-0 w-full h-full object-cover animate-[kenburns_22s_ease-in-out_infinite_alternate]"
            style={{ filter: "saturate(1.05) contrast(1.05)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black" />
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-yellow-500/15 blur-[140px]" />
          <div className="absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t from-black to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-36 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-yellow-400/30 bg-yellow-400/5 px-3 py-1 text-[10px] sm:text-xs font-bold tracking-[0.22em] uppercase text-yellow-400">
              Presencial · Foz do Iguaçu
            </span>
            <h1 className="mt-5 font-heading font-black tracking-[-0.03em] text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.92] text-white">
              Personal <span className="text-gradient-yo">Trainer</span>
            </h1>
            <p className="mt-5 text-neutral-300 text-base sm:text-lg max-w-xl leading-relaxed">
              Treinamento presencial exclusivo. Ajuste técnico de movimentos, motivação lado a lado e foco total nos seus resultados.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#planos"
                data-testid="personal-hero-cta-plans"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-6 py-3.5 text-sm uppercase tracking-wider shadow-[0_0_30px_rgba(250,204,21,0.35)] hover:shadow-[0_0_50px_rgba(250,204,21,0.55)] hover:-translate-y-0.5 transition-all"
              >
                Ver planos <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={WHATSAPP_LINK("Olá Vagner! Quero treinar presencial com você.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="personal-hero-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur text-white font-semibold px-6 py-3.5 text-sm uppercase tracking-wider hover:bg-white/10 hover:border-yellow-400/40 transition-all"
              >
                Falar comigo
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="relative py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5]">
              <img
                src={MEDIA.trainerPortrait}
                alt="Vagner Milioli"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-4 left-4 rounded-full bg-black/70 backdrop-blur border border-white/10 text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest px-3 py-1.5">
                {BRAND.cref}
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <SectionTitle
              overline="Sobre"
              align="left"
              title={<>Quem é <span className="text-gradient-yo">Vagner Milioli?</span></>}
            />
            <div className="mt-6 space-y-4 text-neutral-300 text-base leading-relaxed">
              {ABOUT_PARAGRAPHS.map((p, i) => (
                <p key={i} className={i === ABOUT_PARAGRAPHS.length - 1 ? "text-neutral-400" : ""}>{p}</p>
              ))}
            </div>
            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="personal-about-instagram"
              className="mt-6 inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm font-semibold"
            >
              <Instagram className="w-4 h-4" /> @{BRAND.instagram}
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionTitle
            overline="Por que Presencial?"
            title={<>Treinar com quem <span className="text-gradient-yo">sabe o que faz</span>.</>}
            description="Atenção individual, correção em tempo real e um método ajustado ao seu corpo a cada sessão."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PERSONAL_BENEFITS.map((b, i) => {
              const Icon = iconMap[b.icon] || CheckCircle2;
              return (
                <Reveal
                  key={b.title}
                  delay={i * 0.06}
                  className="glass-card rounded-2xl p-6 sm:p-7 hover:border-yellow-400/40 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`personal-benefit-${i}`}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-black">
                    <Icon className="w-6 h-6" strokeWidth={2.4} />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-bold text-white">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
                    {b.description}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionTitle
            overline="Transformações reais"
            title={<>Resultados de <span className="text-gradient-yo">alunos</span></>}
            description="Arraste a alça para comparar. Cada antes e depois é fruto de consistência e método."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            <BeforeAfter
              before={MEDIA.beforeAfterMan1Before}
              after={MEDIA.beforeAfterMan1After}
              alt="Thiago Zito — transformação presencial"
              testid="personal-before-after-1"
            />
            <BeforeAfter
              before={MEDIA.beforeAfterSilviaBefore}
              after={MEDIA.beforeAfterSilviaAfter}
              alt="Silvia Ribeiro — transformação presencial"
              testid="personal-before-after-2"
            />
          </div>
          <Reveal className="mt-10 flex justify-center" delay={0.15}>
            <a
              href={WHATSAPP_LINK("Olá Vagner! Quero ser o próximo aluno presencial a transformar.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="personal-next-transformation-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-6 py-3.5 text-sm uppercase tracking-widest hover:-translate-y-0.5 shadow-[0_0_30px_rgba(250,204,21,0.35)] transition-all"
            >
              <Flame className="w-4 h-4" /> Quero ser o próximo <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* PLANS */}
      <section id="planos" className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionTitle
            overline="Investimento"
            title={<>Escolha seu <span className="text-gradient-yo">plano</span></>}
            description="Planos mensais presenciais. Avaliação física inclusa em todos."
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PERSONAL_PLANS.map((p, i) => (
              <Reveal
                key={p.id}
                delay={i * 0.06}
                className={`relative rounded-3xl p-6 sm:p-7 flex flex-col ${
                  p.highlight
                    ? "bg-gradient-to-br from-yellow-400/10 via-neutral-900 to-orange-500/10 border border-yellow-400/40 shadow-glow-yellow"
                    : "glass-card hover:border-yellow-400/30"
                } transition-all`}
                data-testid={`personal-plan-${p.id}`}
              >
                {p.badge ? (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-[10px] font-black uppercase tracking-widest px-3 py-1">
                    {p.badge}
                  </span>
                ) : null}
                <div className="text-[10px] uppercase tracking-[0.22em] text-yellow-400 font-bold">
                  Plano
                </div>
                <h3 className="mt-2 font-heading text-3xl font-black text-white">
                  {p.name}
                </h3>
                <div className="mt-1 text-sm text-neutral-400">{p.frequency}</div>
                <div className="mt-5 h-px bg-white/5" />
                <ul className="mt-5 space-y-2.5 text-sm text-neutral-300 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-yellow-400 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={WHATSAPP_LINK(
                    `Olá Vagner! Quero fechar o Plano ${p.name} (presencial, ${p.frequency}).`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`personal-plan-cta-${p.id}`}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full font-bold px-5 py-3 text-xs uppercase tracking-widest transition-all ${
                    p.highlight
                      ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-black shadow-[0_0_20px_rgba(250,204,21,0.35)] hover:-translate-y-0.5"
                      : "border border-white/15 bg-white/5 text-white hover:bg-white/10 hover:border-yellow-400/40"
                  }`}
                >
                  Selecionar plano <ArrowRight className="w-4 h-4" />
                </a>
              </Reveal>
            ))}
          </div>

          {/* Conditions */}
          <Reveal className="mt-14 rounded-3xl border border-white/10 bg-neutral-900/40 backdrop-blur p-7 sm:p-10">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-yellow-400 shrink-0 mt-1" />
              <div>
                <h4 className="font-heading text-xl sm:text-2xl font-bold text-white">
                  Sua evolução com <span className="text-gradient-yo">garantia e segurança</span>
                </h4>
                <p className="mt-1 text-sm text-neutral-400">
                  Regras claras para o nosso compromisso mútuo de resultados.
                </p>
              </div>
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-4 text-sm text-neutral-300">
              {PERSONAL_CONDITIONS.map((c, i) => (
                <li
                  key={i}
                  className="flex gap-3 rounded-2xl border border-white/5 bg-black/40 p-4"
                  data-testid={`personal-condition-${i}`}
                >
                  <span className="font-heading text-yellow-400 text-sm">0{i + 1}</span>
                  <span dangerouslySetInnerHTML={{ __html: c.replace(/([A-Z ]{5,})/g, "<strong class='text-white'>$1</strong>") }} />
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* PAYMENT + LOCATION */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <SectionTitle
              overline="Pagamento"
              align="left"
              title={<>Formas de <span className="text-gradient-yo">pagamento</span></>}
              description="Facilidade e praticidade para começar sua transformação."
            />
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="glass-card rounded-2xl p-6" data-testid="personal-payment-dinheiro">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-black">
                  <Banknote className="w-5 h-5" />
                </div>
                <div className="mt-4 font-heading text-lg font-bold text-white">Dinheiro</div>
                <p className="mt-1 text-sm text-neutral-400">Pagamento em espécie direto na academia.</p>
              </div>
              <div className="glass-card rounded-2xl p-6" data-testid="personal-payment-pix">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-black">
                  <QrCode className="w-5 h-5" />
                </div>
                <div className="mt-4 font-heading text-lg font-bold text-white">PIX</div>
                <p className="mt-1 text-sm text-neutral-400">Transferência rápida e segura via Pix.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <SectionTitle
              overline="Localização"
              align="left"
              title={<>Onde acontecem os <span className="text-gradient-yo">treinos</span></>}
              description="Estrutura completa, ambiente motivador e bem localizado."
            />
            <div className="mt-8 rounded-3xl overflow-hidden border border-white/10 bg-neutral-900/60 backdrop-blur" data-testid="personal-location-card">
              <div className="relative h-56">
                <img
                  src={MEDIA.gymEnv}
                  alt="Academia Estimoove"
                  className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              </div>
              <div className="p-6 sm:p-7 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-yellow-400 mt-1" />
                <div>
                  <div className="font-heading text-lg font-bold text-white">{BRAND.gymName}</div>
                  <p className="text-sm text-neutral-400">{BRAND.gymAddress}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BRAND.gymName + " " + BRAND.gymAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="personal-location-map"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-yellow-400 hover:text-yellow-300"
                  >
                    Abrir no Google Maps <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <FAQ items={FAQ_PERSONAL} title="Tire suas dúvidas sobre o presencial" testid="personal-faq" />

      {/* Final CTA */}
      <section className="relative py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal className="relative overflow-hidden rounded-3xl p-8 sm:p-12 bg-gradient-to-br from-yellow-400 to-orange-500 text-black">
            <div className="absolute inset-0 noise opacity-40" />
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
                  Pronto para treinar presencial?
                </h3>
                <p className="mt-3 text-sm sm:text-base text-black/80 max-w-md">
                  ⚡ VAGAS LIMITADAS • ESTRATÉGIA INDIVIDUALIZADA • EVOLUÇÃO DE VERDADE • RESULTADADOS REAIS E CONSISTENTES
                </p>
              </div>
              <div className="md:text-right">
                <a
                  href={WHATSAPP_LINK("Olá Vagner! Quero agendar minha avaliação presencial.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="personal-final-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-black text-white font-bold px-6 py-3.5 text-sm uppercase tracking-widest hover:bg-neutral-800 transition-colors"
                >
                  Falar no WhatsApp <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
